////////////////////////////////////////////////////////////////////////////////
//                                                                             /
//                                                                             /
//  (C) Copyright 2024 Autodesk, Inc. All rights reserved.                     /
//                                                                             /
//                       ****  CONFIDENTIAL MATERIAL  ****                     /
//                                                                             /
//  The information contained herein is confidential, proprietary to           /
//  Autodesk, Inc., and considered a trade secret.  Use of this information    /
//  by anyone other than authorized employees of Autodesk, Inc. is granted     /
//  only under a written nondisclosure agreement, expressly prescribing        /
//  the scope and manner of such use.                                          /
//                                                                             /
////////////////////////////////////////////////////////////////////////////////

import React, {ReactElement} from 'react';
import {ProjectWiseConfigurationUI} from "../dataModel/ProjectWiseConfigurationUI";
import {
  CenteringContainer,
  EllipsisCell,
  FlexColumn,
  FlexColumnCentered, FlexRow,
  FlexRowCentered
} from "../CommonStyledComponents";
import ProgressRing from "@adsk/alloy-react-progress-ring";
import {BasicButton, LinkButton} from "@adsk/alloy-react-button";
import Table from "./Table";
import {LoadMoreDataRow} from "@adsk/alloy-react-table";
import Theme from "@adsk/alloy-react-theme";
import Tooltip from "@adsk/alloy-react-tooltip";
import {DocumentTwoIcon, PencilIcon} from "@adsk/alloy-react-icon";
import {ActionButton} from "@adsk/alloy-react-menu";
import {ProjectWiseActionTypes} from "../Enums";

const ProjectWiseConfigurationsList = (
  {
    configurations,
    loading,
    hasMoreData,
    canCancelLoad,
    onLoadMore,
    onLoadCancel,
    onAction
  }: {
    configurations: ProjectWiseConfigurationUI[],
    loading: boolean,
    hasMoreData: boolean,
    canCancelLoad: boolean,
    onLoadMore?: (loadAll: boolean) => void,
    onLoadCancel?: () => void,
    onAction?: (action: ProjectWiseActionTypes, config: ProjectWiseConfigurationUI) => void
  }) => {

  function loadMoreClick(loadAll: boolean): void {
    if (onLoadMore) {
      onLoadMore(loadAll);
    }
  }

  function handleAction(action: ProjectWiseActionTypes, config: ProjectWiseConfigurationUI): void {
    if (onAction) {
      onAction(action, config);
    }
  }

  function renderActionCell(config: ProjectWiseConfigurationUI): ReactElement {
    return <FlexRow>
      <Tooltip content={'Edit this Configuration'}>
        <BasicButton onClick={() => handleAction('edit', config)}>
          <PencilIcon/>
        </BasicButton>
      </Tooltip>
      <Tooltip content={'Duplicate this Configuration'}>
        <BasicButton onClick={() => handleAction('duplicate', config)}>
          <DocumentTwoIcon/>
        </BasicButton>
      </Tooltip>
      <ActionButton style={{borderWidth: 0}}
                    horizontal={false}
                    options={[
                      {key: 'delete', label: 'Remove'},
                    ]}
                    onChange={e => handleAction(e[0], config)}/>
    </FlexRow>
  }

  return loading
    ? (
      <CenteringContainer>
        <FlexColumnCentered>
          <ProgressRing size={'large'}/>
          {
            canCancelLoad &&
            <LinkButton onClick={() => {
              if (onLoadCancel) {
                onLoadCancel();
              }
            }}>Cancel</LinkButton>
          }
        </FlexColumnCentered>
      </CenteringContainer>
    ) : (
      <FlexColumn style={{overflow: 'auto'}}>
        <Table<ProjectWiseConfigurationUI>
          data={configurations}
          columns={[
            {
              id: 'Server',
              accessorFn: t => t.ApiConfiguration.serverAddress,
              header: () => 'Server',
              cell: d => <EllipsisCell>{d.row.original.ApiConfiguration.serverAddress}</EllipsisCell>
            },
            {
              id: 'RepositoryID',
              accessorFn: t => t.ApiConfiguration.repositoryId,
              header: () => 'Repository ID',
              cell: d => <EllipsisCell>{d.row.original.ApiConfiguration.repositoryId}</EllipsisCell>
            },
            {
              id: 'RepositoryName',
              accessorFn: t => t.ApiConfiguration.repositoryName,
              header: () => 'Repository Name',
              cell: d => <EllipsisCell>{d.row.original.ApiConfiguration.repositoryName}</EllipsisCell>
            },
            {
              id: 'Credential',
              accessorFn: t => t.CredentialKey,
              header: () => 'Credential',
              cell: d => <EllipsisCell>{d.row.original.CredentialKey}</EllipsisCell>
            },
            {
              id: 'actions',
              size: 100,
              header: () => 'Actions',
              cell: d => renderActionCell(d.row.original)
            },
          ]}
          renderLastRow={() => hasMoreData &&
            <LoadMoreDataRow
              isLoading={loading}
              onLoad={async () => loadMoreClick(false)}
              renderLoadMore={() =>
                <FlexRowCentered>
                  <LinkButton onClick={() => loadMoreClick(false)}>
                    <span style={Theme.typography.bodySmall}>Load more</span>
                  </LinkButton>
                  <LinkButton onClick={() => loadMoreClick(true)} style={{marginLeft: '1em'}}>
                    <span style={Theme.typography.bodySmall}>Load all</span>
                  </LinkButton>
                </FlexRowCentered>
              }/>
          }/>
      </FlexColumn>
    );
};

export default ProjectWiseConfigurationsList;